import { Alert, Card, Skeleton, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  PLATFORM_LABELS,
  PLATFORM_TAG_COLORS,
  STATUS_LABELS,
  useChannelStats,
  type Channel,
  type ChannelStatus,
} from '@entities/channel';

const { Text } = Typography;

interface Props {
  channel: Channel;
  assistantId: string;
}

/**
 * Карточка одного подключённого канала в списке вкладки «Каналы».
 *
 * Клик по карточке → /assistants/:id/channels/:channelId
 *
 * Действия (toggle/delete) и подробная статистика — на отдельной странице,
 * чтобы не загромождать список. Здесь только превью.
 *
 * Stats запрашивается lazy — только когда юзер захочет открыть карточку
 * (через onMouseEnter prefetch'нем). Это снимает нагрузку при списке
 * с 10+ каналами. Пока делаем простой fetch на mount — 1-3 канала
 * обычный кейс, можно оптимизировать в backlog.
 */
export function ChannelCard({ channel, assistantId }: Props) {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useChannelStats(channel.id);

  const isDisabled = channel.status === 'disabled';
  const isWidget = channel.platform === 'widget';

  const handleClick = () => {
    navigate(`/assistants/${assistantId}/channels/${channel.id}`);
  };

  return (
    <Card
      hoverable
      onClick={handleClick}
      size="small"
      styles={{ body: { padding: 16 } }}
      style={{
        opacity: isDisabled ? 0.6 : 1,
        borderColor: isDisabled ? '#FECACA' : undefined,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Space size={8} wrap>
            <Text strong style={{ fontSize: 14 }}>
              {isWidget ? channel.bot_username : `@${channel.bot_username}`}
            </Text>
            <Tag color={PLATFORM_TAG_COLORS[channel.platform]}>
              {PLATFORM_LABELS[channel.platform]}
            </Tag>
            <StatusTag status={channel.status} />
          </Space>
          {channel.bot_display_name && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {channel.bot_display_name}
              </Text>
            </div>
          )}
        </div>

        {/* Превью stats — компактно, без действий */}
        {!isWidget && (
          <div
            style={{
              display: 'flex',
              gap: 16,
              padding: '4px 12px',
              background: '#F9FAFB',
              borderRadius: 6,
            }}
          >
            <CompactStat
              label="за 24ч"
              value={stats?.messages_last_24h}
              loading={statsLoading}
            />
            <CompactStat
              label="₽ за 24ч"
              value={stats ? stats.cost_last_24h_rub.toFixed(2) : undefined}
              loading={statsLoading}
            />
          </div>
        )}
      </div>

      {channel.last_error && (
        <Alert
          type="warning"
          showIcon
          message={channel.last_error}
          style={{ marginTop: 12, fontSize: 12 }}
        />
      )}
    </Card>
  );
}

function StatusTag({ status }: { status: ChannelStatus }) {
  const color =
    status === 'active' ? 'success' : status === 'paused' ? 'warning' : 'error';
  return <Tag color={color}>{STATUS_LABELS[status]}</Tag>;
}

function CompactStat({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | string | undefined;
  loading: boolean;
}) {
  return (
    <div style={{ textAlign: 'right' }}>
      <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
        {label}
      </Text>
      {loading ? (
        <Skeleton.Input active size="small" style={{ width: 40, height: 14 }} />
      ) : (
        <Text strong style={{ fontSize: 13 }}>
          {value ?? '—'}
        </Text>
      )}
    </div>
  );
}
